import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import { maskCurrency, formatCurrencyFromDb } from "../../utils/masks";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import usePermission from "../../hooks/usePermission";
import pedidoCompraService from '../../services/pedidoCompraService';
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import DetalhesPagamentoView from "../../components/DetalhesPagamentoView";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoEmpresa from "../../components/logoEmpresaBase64";
import { useAuth } from "../../context/AuthContext";
import config from "../../config";

// Função para traduzir ações do histórico
const traduzirAcao = (acao, statusAnterior, statusNovo) => {
    const acoes = {
        'criar': 'Pedido de compra criado',
        'enviar_orcamento': 'Enviado para orçamento',
        'enviar_aprovacao': 'Enviado para aprovação',
        'aprovar': 'Pedido aprovado',
        'rejeitar': 'Pedido rejeitado',
        'marcar_em_andamento': 'Marcado como Em Andamento',
        'marcar_recebido': 'Marcado como Recebido',
        'finalizar': 'Pedido finalizado',
        'cancelar': 'Pedido cancelado',
        'alterar_numero_oc': 'Número do pedido alterado',
        'mudar_status': `Status alterado: ${statusAnterior} → ${statusNovo}`
    };

    return acoes[acao] || `${statusAnterior} → ${statusNovo}`;
};

const PedidoCompraDetalhes = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { auth } = useAuth(); // Desestruturando auth do hook
    const [pedido, setPedido] = useState(null);
    const [loading, setLoading] = useState(true);
    const [historico, setHistorico] = useState([]);

    const [showConfirmAprovar, setShowConfirmAprovar] = useState(false);
    const [showConfirmRejeitar, setShowConfirmRejeitar] = useState(false);
    const [showCancelarDialog, setShowCancelarDialog] = useState(false);
    const [motivoCancelamento, setMotivoCancelamento] = useState("");
    const [cancelando, setCancelando] = useState(false);
    const [showAlterarNumeroDialog, setShowAlterarNumeroDialog] = useState(false);
    const [novoNumeroOC, setNovoNumeroOC] = useState("");
    const [motivoAlteracaoNumero, setMotivoAlteracaoNumero] = useState("");
    const [alterandoNumero, setAlterandoNumero] = useState(false);
    const [showConfirmAndamento, setShowConfirmAndamento] = useState(false);
    const [showConfirmRecebido, setShowConfirmRecebido] = useState(false);
    const [showConfirmFinalizar, setShowConfirmFinalizar] = useState(false);
    const [showConfirmEnviarAprovacao, setShowConfirmEnviarAprovacao] = useState(false);
    const [showConfirmEnviarOrcamento, setShowConfirmEnviarOrcamento] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [motivoRejeicao, setMotivoRejeicao] = useState("");
    
    // Estados para vinculação de nota fiscal
    const [numeroNotaFiscal, setNumeroNotaFiscal] = useState("");
    const [notasFiscaisSugestoes, setNotasFiscaisSugestoes] = useState([]);
    const [showSugestoesNota, setShowSugestoesNota] = useState(false);
    const [notaFiscalSelecionada, setNotaFiscalSelecionada] = useState(null);

    const podeAprovar = usePermission("APROVAR_PEDIDO_COMPRA");
    const podeRejeitar = usePermission("REJEITAR_PEDIDO_COMPRA");
    const podeEditar = usePermission("EDITAR_PEDIDO_COMPRA");
    const podeCriar = usePermission("CRIAR_PEDIDO_COMPRA");
    const podeMudarStatus = usePermission("MUDAR_STATUS_PEDIDO_COMPRA");
    const podeReceber = usePermission("RECEBER_PEDIDO_COMPRA");
    const podeConcluir = usePermission("CONCLUIR_PEDIDO_COMPRA");
    const podeVerEmOrcamento = usePermission("VISUALIZAR_PEDIDO_EM_ORCAMENTO");
    const podeEditarQualquerStatus = usePermission("EDITAR_PEDIDO_COMPRA_QUALQUER_STATUS");
    const podeAlterarNumeroOC = usePermission("RENUMERAR_PEDIDO_COMPRA");
    // Qualquer um que pode criar, editar ou mudar status, pode enviar para aprovação
    const podeEnviarAprovacao = podeEditar || podeCriar || podeMudarStatus;
    const podeEnviarOrcamento = podeEditar || podeCriar || podeMudarStatus;

    useEffect(() => {
        carregarDados();
    }, [id]);

    const carregarDados = async () => {
        try {
            setLoading(true);
            
            // Carregar pedido
            const pedidoData = await pedidoCompraService.buscarPorId(id);
            setPedido(pedidoData);
            
            // Tentar carregar histórico (funcionalidade futura)
            try {
                const historicoData = await pedidoCompraService.buscarHistorico(id);
                setHistorico(historicoData || []);
            } catch (err) {
                console.log('Histórico não disponível ainda');
                setHistorico([]);
            }
        } catch (error) {
            console.error("Erro ao carregar pedido:", error);
            alert("Erro ao carregar dados do pedido");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelar = () => {
        setShowCancelarDialog(true);
    };

    const confirmarCancelamento = async () => {
        if (!motivoCancelamento || motivoCancelamento.trim().length === 0) {
            alert("Informe o motivo do cancelamento.");
            return;
        }
        try {
            setCancelando(true);
            await api.patch(`/pedidos-compra/${id}/cancelar`, { motivo: motivoCancelamento.trim() });
            setShowCancelarDialog(false);
            setMotivoCancelamento("");
            alert("Pedido cancelado com sucesso!");
            navigate("/pedidos-compra");
        } catch (error) {
            console.error("Erro ao cancelar pedido:", error);
            alert(error.response?.data?.error || "Erro ao cancelar pedido");
        } finally {
            setCancelando(false);
        }
    };

    const handleAlterarNumeroOC = () => {
        setNovoNumeroOC(pedido?.numeroOC || "");
        setMotivoAlteracaoNumero("");
        setShowAlterarNumeroDialog(true);
    };

    const confirmarAlteracaoNumero = async () => {
        const numero = novoNumeroOC.trim();
        const motivo = motivoAlteracaoNumero.trim();
        if (!numero) {
            alert("Informe o novo número da OC.");
            return;
        }
        if (!motivo) {
            alert("Informe o motivo da alteração.");
            return;
        }
        if (numero === (pedido?.numeroOC || "")) {
            alert("O novo número é igual ao atual.");
            return;
        }
        try {
            setAlterandoNumero(true);
            await api.patch(`/pedidos-compra/${id}/numero-oc`, { numeroOC: numero, motivo });
            setShowAlterarNumeroDialog(false);
            setNovoNumeroOC("");
            setMotivoAlteracaoNumero("");
            setSuccessMessage(`Número alterado para "${numero}" com sucesso.`);
            setShowSuccessDialog(true);
            await carregarDados();
        } catch (error) {
            console.error("Erro ao alterar número:", error);
            alert(error.response?.data?.error || "Erro ao alterar número do pedido");
        } finally {
            setAlterandoNumero(false);
        }
    };

    const handleAprovar = () => {
        console.log("🔵 handleAprovar chamado - abrindo modal");
        setShowConfirmAprovar(true);
    };

    const confirmarAprovacao = async () => {
        console.log("🟢 confirmarAprovacao chamado - iniciando aprovação");
        setShowConfirmAprovar(false);
        try {
            console.log("📤 Enviando requisição de aprovação para pedido ID:", id);
            const response = await pedidoCompraService.aprovar(id);
            console.log("✅ Resposta da aprovação:", response);
            setSuccessMessage("Pedido aprovado com sucesso!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("❌ Erro ao aprovar:", error);
            console.error("Detalhes do erro:", error.response?.data);
            alert(error.response?.data?.error || error.response?.data?.message || "Erro ao aprovar pedido");
        }
    };

    const handleRejeitar = () => {
        setShowConfirmRejeitar(true);
    };

    const confirmarRejeicao = async () => {
        if (!motivoRejeicao.trim()) {
            alert("Informe o motivo da rejeição");
            return;
        }

        setShowConfirmRejeitar(false);
        try {
            await pedidoCompraService.rejeitar(id, motivoRejeicao);
            setSuccessMessage("Pedido rejeitado!");
            setShowSuccessDialog(true);
            setMotivoRejeicao("");
            carregarDados();
        } catch (error) {
            console.error("Erro ao rejeitar:", error);
            alert(error.response?.data?.message || "Erro ao rejeitar pedido");
        }
    };

    const handleMarcarEmAndamento = () => {
        setShowConfirmAndamento(true);
    };

    const confirmarAndamento = async () => {
        setShowConfirmAndamento(false);
        try {
            await pedidoCompraService.marcarEmAndamento(id);
            setSuccessMessage("Pedido marcado como em andamento!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("Erro:", error);
            alert(error.response?.data?.message || "Erro ao atualizar status");
        }
    };

    const handleMarcarRecebido = () => {
        setShowConfirmRecebido(true);
    };

    const confirmarRecebido = async () => {
        setShowConfirmRecebido(false);
        try {
            await pedidoCompraService.marcarRecebido(id);
            setSuccessMessage("Pedido marcado como recebido!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("Erro:", error);
            alert(error.response?.data?.message || "Erro ao atualizar status");
        }
    };

    const handleFinalizar = () => {
        setShowConfirmFinalizar(true);
    };

    const confirmarFinalizacao = async () => {
        setShowConfirmFinalizar(false);
        try {
            await pedidoCompraService.finalizar(id);
            setSuccessMessage("Pedido finalizado com sucesso!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("Erro:", error);
            alert(error.response?.data?.message || "Erro ao finalizar pedido");
        }
    };

    const handleEnviarParaOrcamento = () => {
        setShowConfirmEnviarOrcamento(true);
    };

    const confirmarEnvioOrcamento = async () => {
        setShowConfirmEnviarOrcamento(false);
        try {
            await pedidoCompraService.enviarParaOrcamento(id);
            setSuccessMessage("Pedido enviado para orçamento com sucesso!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("Erro ao enviar para orçamento:", error);
            alert(error.response?.data?.error || error.response?.data?.message || "Erro ao enviar pedido para orçamento");
        }
    };

    const handleEnviarParaAprovacao = () => {
        setShowConfirmEnviarAprovacao(true);
    };

    const confirmarEnvioAprovacao = async () => {
        setShowConfirmEnviarAprovacao(false);
        try {
            await pedidoCompraService.enviarParaAprovacao(id);
            setSuccessMessage("Pedido enviado para aprovação com sucesso!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("Erro ao enviar para aprovação:", error);
            alert(error.response?.data?.error || error.response?.data?.message || "Erro ao enviar pedido para aprovação");
        }
    };

    // Funções para vinculação de nota fiscal
    const buscarNotasFiscaisPorChave = async (chave) => {
        if (chave.length < 5) {
            setNotasFiscaisSugestoes([]);
            setShowSugestoesNota(false);
            return;
        }

        try {
            const response = await api.get('/notasfiscais', {
                params: {
                    chave: chave,
                    pageSize: 20
                }
            });
            setNotasFiscaisSugestoes(response.data.records || []);
            setShowSugestoesNota(true);
        } catch (error) {
            console.error("Erro ao buscar notas fiscais:", error);
            setNotasFiscaisSugestoes([]);
        }
    };

    const selecionarNotaFiscal = (nota) => {
        setNotaFiscalSelecionada(nota);
        setNumeroNotaFiscal(nota.chave);
        setShowSugestoesNota(false);
    };

    const vincularNotaFiscal = async () => {
        if (!notaFiscalSelecionada) {
            alert("Selecione uma nota fiscal");
            return;
        }

        try {
            await pedidoCompraService.vincularNotaFiscal(id, notaFiscalSelecionada.id);
            setSuccessMessage("Nota fiscal vinculada com sucesso!");
            setShowSuccessDialog(true);
            setNumeroNotaFiscal("");
            setNotaFiscalSelecionada(null);
            carregarDados();
        } catch (error) {
            console.error("Erro ao vincular nota fiscal:", error);
            alert(error.response?.data?.message || "Erro ao vincular nota fiscal");
        }
    };

    const desvincularNotaFiscal = async () => {
        if (!window.confirm("Deseja realmente desvincular esta nota fiscal?")) {
            return;
        }
        
        try {
            await pedidoCompraService.vincularNotaFiscal(id, null);
            setSuccessMessage("Nota fiscal desvinculada com sucesso!");
            setShowSuccessDialog(true);
            carregarDados();
        } catch (error) {
            console.error("Erro ao desvincular nota fiscal:", error);
            alert(error.response?.data?.message || "Erro ao desvincular nota fiscal");
        }
    };

    // Função auxiliar para extrair o nome do status
    const getStatusNome = (status) => {
        if (!status) return '';
        // Se for objeto, pegar o nome
        if (typeof status === 'object') return status.nome || '';
        // Se for string, retornar direto
        return status;
    };

    // Função para verificar se o status é de um tipo específico
    const isStatus = (status, ...nomes) => {
        const statusNome = getStatusNome(status);
        return nomes.some(nome => 
            statusNome === nome || 
            statusNome.toLowerCase() === nome.toLowerCase() ||
            statusNome.replace(/\s/g, '_').toUpperCase() === nome.toUpperCase()
        );
    };

    const getStatusColor = (status) => {
        const statusNome = getStatusNome(status);
        const colors = {
            'Rascunho': '#9CA3AF',
            'Em Orçamento': '#F97316',
            'Aguardando Aprovação': '#F59E0B',
            'Aprovado': '#10B981',
            'Rejeitado': '#EF4444',
            'Em andamento': '#3B82F6',
            'Recebida': '#8B5CF6',
            'Concluído': '#6366F1',
            // Manter versões antigas para compatibilidade
            'RASCUNHO': '#9CA3AF',
            'EM_ORCAMENTO': '#F97316',
            'AGUARDANDO_APROVACAO': '#F59E0B',
            'APROVADO': '#10B981',
            'REJEITADO': '#EF4444',
            'EM_ANDAMENTO': '#3B82F6',
            'RECEBIDO': '#8B5CF6',
            'FINALIZADO': '#6366F1'
        };
        return colors[statusNome] || '#9CA3AF';
    };

    const getStatusLabel = (status) => {
        const statusNome = getStatusNome(status);
        // Se já tiver um nome legível, retornar
        if (statusNome && !statusNome.includes('_')) {
            return statusNome;
        }
        // Senão, fazer mapeamento
        const labels = {
            'RASCUNHO': 'Rascunho',
            'EM_ORCAMENTO': 'Em Orçamento',
            'AGUARDANDO_APROVACAO': 'Aguardando Aprovação',
            'APROVADO': 'Aprovado',
            'REJEITADO': 'Rejeitado',
            'EM_ANDAMENTO': 'Em Andamento',
            'RECEBIDO': 'Recebido',
            'FINALIZADO': 'Finalizado'
        };
        return labels[statusNome] || statusNome;
    };

    const gerarPDF = async () => {
        if (!pedido) return;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let posY = 10;

        // ============================================
        // DADOS FIXOS DA EMPRESA
        // ============================================
        const dadosEmpresa = {
            razaoSocial: 'DAFMC SOLUCOES, COMERCIO E SERVICOS LTDA',
            endereco: 'Rua Quinze de Novembro, nº 90, Sala 801 - Bairro: Centro, na cidade de Niterói/RJ',
            telefone: '(21) 96699-6347',
            email: 'financeiro@dafmc.com.br'
        };

        // ============================================
        // CABEÇALHO DA EMPRESA
        // ============================================
        
        // Razão Social da Empresa (lado esquerdo)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(dadosEmpresa.razaoSocial, 14, posY);
        
        // Título do Documento (lado direito)
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text("PEDIDO DE COMPRA", pageWidth - 14, posY, { align: 'right' });
        
        posY += 5;
        
        // Endereço
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(dadosEmpresa.endereco, 14, posY);
        
        // Logo (lado direito) - Proporção original da imagem (4:1)
        if (logoEmpresa) {
            const logoWidth = 32;
            const logoHeight = 8; // Proporção 4:1 para não esticar
            doc.addImage(logoEmpresa, 'PNG', pageWidth - logoWidth - 14, posY - 2, logoWidth, logoHeight);
        }
        
        posY += 4;
        
        // Telefone e Email
        doc.setFontSize(8);
        doc.text(`${dadosEmpresa.telefone}`, 14, posY);
        doc.setTextColor(30, 120, 180);
        doc.text(dadosEmpresa.email, 60, posY);
        
        posY += 8;
        
        // Linha horizontal de separação
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.line(14, posY, pageWidth - 14, posY);
        posY += 6;

        // ============================================
        // INFORMAÇÕES DO PEDIDO (Estilo Tabela)
        // ============================================
        const statusLabel = getStatusLabel(pedido.status);
        const col1X = 14;
        const col2X = pageWidth / 2 + 5;
        const labelWidth = 35;
        const dataEmissaoFormatada = formatDateWithoutHour(pedido.dataEmissao) || 'N/A';
        
        // Linha 1: Número do Pedido e Data
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('Número do Pedido:', col1X, posY);
        doc.setFont(undefined, 'normal');
        doc.text(pedido.numeroOC || pedido.numero || 'N/A', col1X + labelWidth, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('Data:', pageWidth - 70, posY);
        doc.setFont(undefined, 'normal');
        doc.text(dataEmissaoFormatada, pageWidth - 55, posY);
        posY += 6;
        
        // Linha 2: Comprador e Obra
        doc.setFont(undefined, 'bold');
        doc.text('Comprador:', col1X, posY);
        doc.setFont(undefined, 'normal');
        const compradorNome = pedido.comprador?.nome || auth.user?.name || 'N/A';
        doc.text(compradorNome, col1X + labelWidth, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('Obra:', col2X, posY);
        doc.setFont(undefined, 'normal');
        doc.setFont(undefined, 'bolditalic');
        const obraNome = pedido.obra?.nome || pedido.obra?.nomeProjeto || pedido.nomeObra || 'N/A';
        const obraTexto = doc.splitTextToSize(obraNome, pageWidth - col2X - 30);
        doc.text(obraTexto, col2X + 20, posY);
        posY += Math.max(6, obraTexto.length * 5);
        
        // Linha 3: Escopo (linha completa para não truncar)
        doc.setFont(undefined, 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('Escopo:', col1X, posY);
        doc.setFont(undefined, 'bolditalic');
        const escopoTextoOriginal = pedido.escopo || 'N/A';
        // Escopo usa quase toda a largura da página
        const escopoTexto = doc.splitTextToSize(escopoTextoOriginal, pageWidth - col1X - labelWidth - 60);
        doc.text(escopoTexto, col1X + labelWidth, posY);
        
        // Forma de pagamento no canto direito (mesma linha)
        doc.setFont(undefined, 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('FORMA PGTO:', pageWidth - 55, posY);
        doc.setFont(undefined, 'bolditalic');
        const formaPagamentoTexto = pedido.formaPagamento || 'N/A';
        doc.text(formaPagamentoTexto, pageWidth - 55, posY + 4);
        
        posY += Math.max(6, escopoTexto.length * 4) + 2;
        
        // Linha 4: Status do Pedido
        doc.setFont(undefined, 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('Status:', col1X, posY);
        
        // Badge de status colorido
        const statusColor = hexToRgb(getStatusColor(pedido.status));
        doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
        const statusWidth = doc.getTextWidth(statusLabel) + 8;
        doc.roundedRect(col1X + labelWidth - 2, posY - 3.5, statusWidth, 5, 1.5, 1.5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text(statusLabel, col1X + labelWidth + 2, posY);
        doc.setFontSize(9);
        
        posY += 8;

        // ============================================
        // DADOS DO FORNECEDOR (Estilo Tabela)
        // ============================================
        // Cabeçalho da seção
        doc.setFillColor(30, 120, 180);
        doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('DADOS DO FORNECEDOR', pageWidth / 2, posY + 1.5, { align: 'center' });
        posY += 8;
        
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(8);
        
        // Linha 1: Razão Social e CNPJ
        const fornecedor = pedido.fornecedor || {};
        
        doc.setFont(undefined, 'bold');
        doc.text('Razão Social:', col1X, posY);
        doc.setFont(undefined, 'normal');
        const razaoSocialFornecedor = fornecedor.razaoSocial || fornecedor.nomeFornecedor || fornecedor.nome || 'N/A';
        const razaoSocialTexto = doc.splitTextToSize(razaoSocialFornecedor, 90);
        doc.text(razaoSocialTexto, col1X + 25, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('CNPJ:', pageWidth - 70, posY);
        doc.setFont(undefined, 'normal');
        doc.text(fornecedor.cpfCnpj || 'N/A', pageWidth - 55, posY);
        posY += Math.max(5, razaoSocialTexto.length * 4);
        
        // Linha 2: Inscrição Estadual, Inscrição Municipal, Celular
        doc.setFont(undefined, 'bold');
        doc.text('Insc. Estadual:', col1X, posY);
        doc.setFont(undefined, 'normal');
        doc.text(fornecedor.inscricaoEstadual || '-', col1X + 25, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('Insc. Mun.:', 70, posY);
        doc.setFont(undefined, 'normal');
        doc.text(fornecedor.inscricaoMunicipal || '-', 90, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('Celular:', pageWidth - 70, posY);
        doc.setFont(undefined, 'normal');
        doc.text(fornecedor.telefoneContato || '-', pageWidth - 55, posY);
        posY += 5;
        
        // Linha 3: Endereço e Município/UF
        doc.setFont(undefined, 'bold');
        doc.text('Endereço:', col1X, posY);
        doc.setFont(undefined, 'normal');
        const enderecoCompleto = [
            fornecedor.endereco,
            fornecedor.numeroEndereco ? `, ${fornecedor.numeroEndereco}` : '',
            fornecedor.bairro ? ` - ${fornecedor.bairro}` : ''
        ].filter(Boolean).join('') || '-';
        const enderecoTexto = doc.splitTextToSize(enderecoCompleto, 100);
        doc.text(enderecoTexto, col1X + 25, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('MUN. / UF:', pageWidth - 70, posY);
        doc.setFont(undefined, 'normal');
        const municipioUf = [fornecedor.municipio, fornecedor.uf].filter(Boolean).join('/') || '-';
        doc.text(municipioUf, pageWidth - 55, posY);
        posY += Math.max(5, enderecoTexto.length * 4);
        
        // Linha 4: Email e OBS
        doc.setFont(undefined, 'bold');
        doc.text('Email:', col1X, posY);
        doc.setFont(undefined, 'normal');
        doc.text(fornecedor.email || '-', col1X + 25, posY);
        
        doc.setFont(undefined, 'bold');
        doc.text('OBS:', pageWidth - 70, posY);
        doc.setFont(undefined, 'normal');
        doc.text(fornecedor.observacoes ? fornecedor.observacoes.substring(0, 30) : '-', pageWidth - 55, posY);
        posY += 8;

        // ============================================
        // NOTA FISCAL VINCULADA (se existir)
        // ============================================
        if (pedido.notaFiscal) {
            // Cabeçalho da seção
            doc.setFillColor(30, 120, 180);
            doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('NOTA FISCAL VINCULADA', pageWidth / 2, posY + 1.5, { align: 'center' });
            posY += 8;

            doc.setFontSize(8);
            doc.setTextColor(60, 60, 60);

            // Linha 1: Número/Série e Data de Emissão
            doc.setFont(undefined, 'bold');
            doc.text('Número/Série:', col1X, posY);
            doc.setFont(undefined, 'normal');
            doc.text(`NF ${pedido.notaFiscal.numero}${pedido.notaFiscal.serie ? `/${pedido.notaFiscal.serie}` : ''}`, col1X + 25, posY);
            
            doc.setFont(undefined, 'bold');
            doc.text('Data Emissão:', col2X, posY);
            doc.setFont(undefined, 'normal');
            doc.text(pedido.notaFiscal.dataEmissao 
                ? new Date(pedido.notaFiscal.dataEmissao).toLocaleDateString('pt-BR') 
                : 'N/A', col2X + 25, posY);
            posY += 5;

            // Linha 2: Valor e Chave (se existir)
            if (pedido.notaFiscal.valorNfe) {
                doc.setFont(undefined, 'bold');
                doc.text('Valor da NF:', col1X, posY);
                doc.setFont(undefined, 'normal');
                doc.text(`R$ ${parseFloat(pedido.notaFiscal.valorNfe).toLocaleString('pt-BR', { 
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2 
                })}`, col1X + 25, posY);
                posY += 5;
            }

            // Chave de acesso (se existir)
            if (pedido.notaFiscal.chave) {
                doc.setFont(undefined, 'bold');
                doc.text('Chave de Acesso:', col1X, posY);
                doc.setFont(undefined, 'normal');
                doc.setFontSize(6);
                const chaveLinhas = doc.splitTextToSize(pedido.notaFiscal.chave, pageWidth - col1X - 35);
                doc.text(chaveLinhas, col1X + 30, posY);
                posY += chaveLinhas.length * 3.5;
                doc.setFontSize(8);
            }

            posY += 5;
        }

        // ============================================
        // CONDIÇÕES DE PAGAMENTO E PRAZO (se existir)
        // ============================================
        if (pedido.condicoesPagamento || pedido.prazoEntrega || pedido.dataPrevistaEntrega) {
            // Cabeçalho da seção
            doc.setFillColor(30, 120, 180);
            doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('CONDIÇÕES COMERCIAIS', pageWidth / 2, posY + 1.5, { align: 'center' });
            posY += 8;

            doc.setFontSize(8);
            doc.setTextColor(60, 60, 60);

            if (pedido.prazoEntrega) {
                doc.setFont(undefined, 'bold');
                doc.text('Prazo de Entrega:', col1X, posY);
                doc.setFont(undefined, 'normal');
                doc.text(`${pedido.prazoEntrega} dias`, col1X + 30, posY);
            }
            
            if (pedido.dataPrevistaEntrega) {
                doc.setFont(undefined, 'bold');
                doc.text('Data Prevista:', col2X, posY);
                doc.setFont(undefined, 'normal');
                doc.text(formatDateWithoutHour(pedido.dataPrevistaEntrega), col2X + 25, posY);
            }
            
            if (pedido.prazoEntrega || pedido.dataPrevistaEntrega) {
                posY += 5;
            }

            if (pedido.condicoesPagamento) {
                doc.setFont(undefined, 'bold');
                doc.text('Condições de Pagamento:', col1X, posY);
                doc.setFont(undefined, 'normal');
                const condicoesTexto = doc.splitTextToSize(pedido.condicoesPagamento, pageWidth - col1X - 45);
                doc.text(condicoesTexto, col1X + 42, posY);
                posY += (condicoesTexto.length * 4) + 2;
            }

            posY += 5;
        }

        // ============================================
        // DETALHES DE PAGAMENTO
        // ============================================
        const temDetalhesPagamento = pedido.formaPagamento && (
            pedido.pixChave || 
            pedido.parcelamentoParcelas || 
            pedido.bancoNome || 
            pedido.boletoVencimento || 
            pedido.cartaoTipo
        );

        if (temDetalhesPagamento) {
            // Verificar se precisa de nova página
            if (posY > pageHeight - 80) {
                doc.addPage();
                posY = 20;
            }

            // Cabeçalho da seção
            doc.setFillColor(30, 120, 180);
            doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('DETALHES DE PAGAMENTO', pageWidth / 2, posY + 1.5, { align: 'center' });
            posY += 8;

            doc.setFontSize(8);
            doc.setTextColor(60, 60, 60);

            // Usar duas colunas para economizar espaço
            const labelW = 30; // Label mais estreito
            let leftY = posY;
            let rightY = posY;

            // PIX (2 colunas)
            if (pedido.formaPagamento === 'PIX' && pedido.pixChave) {
                const tipoChaveMap = {
                    'CPF': 'CPF', 'CNPJ': 'CNPJ', 'EMAIL': 'E-mail',
                    'TELEFONE': 'Telefone', 'ALEATORIA': 'Chave Aleatória'
                };

                // Coluna esquerda
                doc.setFont(undefined, 'bold');
                doc.text('Tipo Chave:', col1X, leftY);
                doc.setFont(undefined, 'normal');
                doc.text(tipoChaveMap[pedido.pixTipoChave] || pedido.pixTipoChave, col1X + labelW, leftY);
                leftY += 5;

                if (pedido.pixVencimento) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Vencimento:', col1X, leftY);
                    doc.setFont(undefined, 'normal');
                    doc.text(formatDateWithoutHour(pedido.pixVencimento), col1X + labelW, leftY);
                    leftY += 5;
                }

                // Coluna direita
                doc.setFont(undefined, 'bold');
                doc.text('Chave PIX:', col2X, rightY);
                doc.setFont(undefined, 'normal');
                const chaveTexto = doc.splitTextToSize(pedido.pixChave, (pageWidth - col2X - labelW - 18));
                doc.text(chaveTexto, col2X + labelW, rightY);
                rightY += chaveTexto.length * 5;

                posY = Math.max(leftY, rightY) + 3;
                leftY = rightY = posY;
            }

            // Parcelamento (2 colunas)
            if (pedido.parcelamentoParcelas && pedido.parcelamentoParcelas > 0) {
                const intervaloMap = {
                    'SEMANAL': 'Semanal', 'QUINZENAL': 'Quinzenal', 'MENSAL': 'Mensal',
                    'BIMESTRAL': 'Bimestral', 'TRIMESTRAL': 'Trimestral', 'SEMESTRAL': 'Semestral'
                };

                // Coluna esquerda
                doc.setFont(undefined, 'bold');
                doc.text('Parcelas:', col1X, leftY);
                doc.setFont(undefined, 'normal');
                doc.text(`${pedido.parcelamentoParcelas}x`, col1X + labelW, leftY);
                leftY += 5;

                if (pedido.parcelamentoDataPrimeira) {
                    doc.setFont(undefined, 'bold');
                    doc.text('1ª Parcela:', col1X, leftY);
                    doc.setFont(undefined, 'normal');
                    doc.text(formatDateWithoutHour(pedido.parcelamentoDataPrimeira), col1X + labelW, leftY);
                    leftY += 5;
                }

                // Coluna direita
                if (pedido.parcelamentoIntervalo) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Intervalo:', col2X, rightY);
                    doc.setFont(undefined, 'normal');
                    doc.text(intervaloMap[pedido.parcelamentoIntervalo] || pedido.parcelamentoIntervalo, col2X + labelW, rightY);
                    rightY += 5;
                }

                if (pedido.valorTotal && pedido.parcelamentoParcelas) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Vlr. Parcela:', col2X, rightY);
                    doc.setFont(undefined, 'normal');
                    const valorParcela = pedido.valorTotal / pedido.parcelamentoParcelas;
                    doc.text(formatCurrencyFromDb(valorParcela), col2X + labelW, rightY);
                    rightY += 5;
                }

                posY = Math.max(leftY, rightY);
                
                // Observação em linha inteira se houver
                if (pedido.parcelamentoObservacao) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Obs.:', col1X, posY);
                    doc.setFont(undefined, 'normal');
                    const obsLinhas = doc.splitTextToSize(pedido.parcelamentoObservacao, pageWidth - col1X - labelW - 18);
                    doc.text(obsLinhas, col1X + labelW, posY);
                    posY += obsLinhas.length * 5;
                }

                // Tabela de cronograma de parcelas (usa os anexos reais cadastrados)
                const boletosParcelados = (pedido.anexos || [])
                    .filter(a => a.numeroParcela != null)
                    .sort((a, b) => a.numeroParcela - b.numeroParcela);

                if (boletosParcelados.length > 0) {
                    posY += 4;

                    const numParcelas = pedido.parcelamentoParcelas || boletosParcelados.length;
                    const valorParcela = pedido.valorTotal ? pedido.valorTotal / numParcelas : null;

                    const parcelasTableData = boletosParcelados.map(anexo => {
                        const dataVenc = anexo.dataVencimento
                            ? new Date(anexo.dataVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                            : '-';
                        const temArquivo = !!anexo.arquivo;
                        return [
                            `${anexo.numeroParcela}ª / ${numParcelas}`,
                            dataVenc,
                            valorParcela !== null ? formatCurrencyFromDb(valorParcela) : '-',
                            temArquivo ? 'Sim' : 'Não'
                        ];
                    });

                    // Sub-cabeçalho da tabela
                    doc.setFont(undefined, 'bold');
                    doc.setFontSize(8);
                    doc.setTextColor(60, 60, 60);
                    doc.text('CRONOGRAMA DE PARCELAS:', col1X, posY);
                    posY += 4;

                    if (posY > pageHeight - 40) {
                        doc.addPage();
                        posY = 20;
                    }

                    autoTable(doc, {
                        startY: posY,
                        head: [['Parcela', 'Vencimento', 'Valor', 'Boleto']],
                        body: parcelasTableData,
                        theme: 'grid',
                        margin: { left: 14, right: 14 },
                        tableWidth: 'auto',
                        styles: { fontSize: 8, cellPadding: 2, textColor: [60, 60, 60] },
                        headStyles: {
                            fillColor: [30, 120, 180],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold',
                            fontSize: 8,
                            halign: 'center'
                        },
                        columnStyles: {
                            0: { cellWidth: 35, halign: 'center' },
                            1: { halign: 'center' },
                            2: { halign: 'right' },
                            3: { cellWidth: 25, halign: 'center' }
                        },
                        alternateRowStyles: { fillColor: [245, 248, 255] }
                    });

                    posY = doc.lastAutoTable.finalY + 3;
                }

                posY += 3;
                leftY = rightY = posY;
            }

            // Dados Bancários (2 colunas)
            if (pedido.bancoNome) {
                // Coluna esquerda
                doc.setFont(undefined, 'bold');
                doc.text('Banco:', col1X, leftY);
                doc.setFont(undefined, 'normal');
                doc.text(`${pedido.bancoNome} (${pedido.bancoCodigo})`, col1X + labelW, leftY);
                leftY += 5;

                doc.setFont(undefined, 'bold');
                doc.text('Agência:', col1X, leftY);
                doc.setFont(undefined, 'normal');
                doc.text(pedido.bancoAgencia, col1X + labelW, leftY);
                leftY += 5;

                // Coluna direita
                const tipoConta = pedido.bancoTipoConta === 'CORRENTE' ? 'Corrente' : 'Poupança';
                doc.setFont(undefined, 'bold');
                doc.text('Conta:', col2X, rightY);
                doc.setFont(undefined, 'normal');
                doc.text(`${pedido.bancoConta} (${tipoConta})`, col2X + labelW, rightY);
                rightY += 5;

                doc.setFont(undefined, 'bold');
                doc.text('Favorecido:', col2X, rightY);
                doc.setFont(undefined, 'normal');
                const favTexto = doc.splitTextToSize(pedido.bancoFavorecido, (pageWidth - col2X - labelW - 18));
                doc.text(favTexto, col2X + labelW, rightY);
                rightY += favTexto.length * 5;

                posY = Math.max(leftY, rightY);

                // CPF/CNPJ em linha inteira se houver
                if (pedido.bancoCnpjCpfFavorecido) {
                    doc.setFont(undefined, 'bold');
                    doc.text('CPF/CNPJ:', col1X, posY);
                    doc.setFont(undefined, 'normal');
                    doc.text(pedido.bancoCnpjCpfFavorecido, col1X + labelW, posY);
                    posY += 5;
                }

                posY += 3;
                leftY = rightY = posY;
            }

            // Boleto único (2 colunas)
            if (pedido.formaPagamento === 'BOLETO' && pedido.boletoVencimento && !pedido.parcelamentoParcelas) {
                doc.setFont(undefined, 'bold');
                doc.text('Vencimento:', col1X, leftY);
                doc.setFont(undefined, 'normal');
                doc.text(formatDateWithoutHour(pedido.boletoVencimento), col1X + labelW, leftY);
                leftY += 5;

                if (pedido.boletoNumero) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Nº Boleto:', col2X, rightY);
                    doc.setFont(undefined, 'normal');
                    doc.text(pedido.boletoNumero, col2X + labelW, rightY);
                    rightY += 5;
                }

                posY = Math.max(leftY, rightY);

                // Código de barras em linha inteira
                if (pedido.boletoCodigoBarras) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Cód. Barras:', col1X, posY);
                    doc.setFont(undefined, 'normal');
                    doc.setFontSize(7);
                    const codigoLinhas = doc.splitTextToSize(pedido.boletoCodigoBarras, pageWidth - col1X - labelW - 18);
                    doc.text(codigoLinhas, col1X + labelW, posY);
                    doc.setFontSize(9);
                    posY += codigoLinhas.length * 4;
                }

                posY += 3;
                leftY = rightY = posY;
            }

            // Cartão
            if (pedido.formaPagamento === 'CARTAO' && pedido.cartaoTipo) {
                const tipoCartaoMap = { 'CREDITO': 'Crédito', 'DEBITO': 'Débito' };
                
                doc.setFont(undefined, 'bold');
                doc.text('Tipo Cartão:', col1X, posY);
                doc.setFont(undefined, 'normal');
                doc.text(tipoCartaoMap[pedido.cartaoTipo] || pedido.cartaoTipo, col1X + labelW, posY);
                posY += 5;

                if (pedido.cartaoObservacao) {
                    doc.setFont(undefined, 'bold');
                    doc.text('Obs.:', col1X, posY);
                    doc.setFont(undefined, 'normal');
                    const obsLinhas = doc.splitTextToSize(pedido.cartaoObservacao, pageWidth - col1X - labelW - 18);
                    doc.text(obsLinhas, col1X + labelW, posY);
                    posY += obsLinhas.length * 5;
                }

                posY += 3;
            }

            // Observações gerais (linha inteira)
            if (pedido.pagamentoObservacoes) {
                doc.setFont(undefined, 'bold');
                doc.text('Obs. Pgto:', col1X, posY);
                doc.setFont(undefined, 'normal');
                const obsLinhas = doc.splitTextToSize(pedido.pagamentoObservacoes, pageWidth - col1X - labelW - 18);
                doc.text(obsLinhas, col1X + labelW, posY);
                posY += obsLinhas.length * 5 + 2;
            }

            posY += 2;
        }

        // ============================================
        // ITENS DO PEDIDO
        // ============================================
        // Verificar se precisa de nova página antes dos itens
        if (posY > pageHeight - 100) {
            doc.addPage();
            posY = 20;
        }

        // Cabeçalho da seção
        doc.setFillColor(30, 120, 180);
        doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('ITENS DO PEDIDO', pageWidth / 2, posY + 1.5, { align: 'center' });
        posY += 8;

        const itensData = (pedido.itens || []).map((item, index) => [
            (index + 1).toString(),
            item.descricao || 'N/A',
            item.quantidade?.toString() || '0',
            item.unidadeMedida || 'UN',
            formatCurrencyFromDb(item.precoUnitario),
            formatCurrencyFromDb((item.quantidade || 0) * (item.precoUnitario || 0))
        ]);

        autoTable(doc, {
            startY: posY,
            head: [['#', 'Descrição', 'Qtd', 'Unid.', 'Preço Unit.', 'Subtotal']],
            body: itensData,
            theme: 'grid',
            headStyles: {
                fillColor: [70, 70, 70], // Cinza escuro para diferenciar do cabeçalho de seção
                textColor: [255, 255, 255],
                fontSize: 8,
                fontStyle: 'bold',
                halign: 'center',
            },
            bodyStyles: {
                fontSize: 8,
                textColor: [60, 60, 60]
            },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
                1: { cellWidth: 75 },
                2: { cellWidth: 15, halign: 'center' },
                3: { cellWidth: 18, halign: 'center' },
                4: { cellWidth: 30, halign: 'right' },
                5: { cellWidth: 32, halign: 'right', fontStyle: 'bold' }
            },
            margin: { left: 14, right: 14 },
            styles: {
                lineColor: [200, 200, 200],
                lineWidth: 0.1
            }
        });

        posY = doc.lastAutoTable.finalY + 5;

        // ============================================
        // RESUMO DE VALORES
        // ============================================
        // Verificar se há espaço para o resumo de valores (precisa de ~30mm)
        if (posY > pageHeight - 45) {
            doc.addPage();
            posY = 20;
        }

        // Box com valores
        const valorBoxStartY = posY;
        const valorBoxX = pageWidth - 100;
        
        doc.setFontSize(8);
        doc.setTextColor(60, 60, 60);
        
        // Subtotal
        doc.setFont(undefined, 'normal');
        doc.text('Subtotal:', valorBoxX, posY);
        doc.text(formatCurrencyFromDb(pedido.valorTotal - (pedido.valorFrete || 0) + (pedido.valorDesconto || 0)), pageWidth - 14, posY, { align: 'right' });
        posY += 5;
        
        // Desconto (se houver)
        if (pedido.valorDesconto && pedido.valorDesconto > 0) {
            doc.text('Desconto:', valorBoxX, posY);
            doc.setTextColor(220, 38, 38);
            doc.text(`- ${formatCurrencyFromDb(pedido.valorDesconto)}`, pageWidth - 14, posY, { align: 'right' });
            doc.setTextColor(60, 60, 60);
            posY += 5;
        }
        
        // Frete (se houver)
        if (pedido.valorFrete && pedido.valorFrete > 0) {
            doc.text('Frete:', valorBoxX, posY);
            doc.text(`+ ${formatCurrencyFromDb(pedido.valorFrete)}`, pageWidth - 14, posY, { align: 'right' });
            posY += 5;
        }
        
        // Linha de separação
        doc.setDrawColor(30, 120, 180);
        doc.setLineWidth(0.5);
        doc.line(valorBoxX, posY, pageWidth - 14, posY);
        posY += 5;
        
        // Total Final - Label e valor em linha separada para não sobrepor
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 120, 180);
        doc.text('VALOR TOTAL:', valorBoxX, posY);
        doc.setTextColor(0, 0, 0);
        doc.text(formatCurrencyFromDb(pedido.valorTotal), pageWidth - 14, posY, { align: 'right' });

        posY += 10;

        // Retenções (se houver)
        if (pedido.retencoes && pedido.retencoes.length > 0) {
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(60, 60, 60);

            pedido.retencoes.forEach(r => {
                const nomeImposto = r.tipoImposto?.nome || 'Imposto';
                doc.text(`${nomeImposto}:`, valorBoxX, posY);
                doc.setTextColor(220, 38, 38);
                doc.text(`- ${formatCurrencyFromDb(r.valor)}`, pageWidth - 14, posY, { align: 'right' });
                doc.setTextColor(60, 60, 60);
                posY += 5;
            });

            // Total Retenções
            doc.setFont(undefined, 'bold');
            doc.text('Total Retenções:', valorBoxX, posY);
            doc.setTextColor(220, 38, 38);
            doc.text(`- ${formatCurrencyFromDb(pedido.valorTotalRetencoes || 0)}`, pageWidth - 14, posY, { align: 'right' });
            doc.setTextColor(60, 60, 60);
            posY += 5;

            // Linha de separação
            doc.setDrawColor(30, 120, 180);
            doc.setLineWidth(0.5);
            doc.line(valorBoxX, posY, pageWidth - 14, posY);
            posY += 5;

            // Valor Líquido ao Fornecedor
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(5, 150, 105);
            doc.text('VALOR LÍQUIDO:', valorBoxX, posY);
            doc.text(formatCurrencyFromDb(pedido.valorLiquidoFornecedor || 0), pageWidth - 14, posY, { align: 'right' });
            doc.setTextColor(0, 0, 0);
            posY += 10;
        }

        // ============================================
        // OBSERVAÇÕES
        // ============================================
        if (pedido.observacao || pedido.observacoes) {
            if (posY > pageHeight - 60) {
                doc.addPage();
                posY = 20;
            }

            // Cabeçalho da seção
            doc.setFillColor(30, 120, 180);
            doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('OBSERVAÇÕES', pageWidth / 2, posY + 1.5, { align: 'center' });
            posY += 8;

            doc.setFontSize(8);
            doc.setTextColor(60, 60, 60);
            doc.setFont(undefined, 'normal');
            const observacaoTexto = pedido.observacao || pedido.observacoes;
            const observacaoLinhas = doc.splitTextToSize(observacaoTexto, pageWidth - 28);
            doc.text(observacaoLinhas, 14, posY);
            posY += (observacaoLinhas.length * 4) + 5;
        }

        // ============================================
        // ASSINATURA DE APROVAÇÃO
        // ============================================
        if (pedido.aprovadoPor && pedido.dataAprovacao) {
            // Reservar espaço suficiente: imagem + linha + nome + data (~70mm)
            if (posY > pageHeight - 100) {
                doc.addPage();
                posY = 20;
            }

            // Cabeçalho da seção
            doc.setFillColor(30, 120, 180);
            doc.rect(14, posY - 3, pageWidth - 28, 7, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('APROVAÇÃO', pageWidth / 2, posY + 1.5, { align: 'center' });
            posY += 12;

            const approvalCenterX = pageWidth / 2;

            // Carregar e inserir a imagem da assinatura se disponível
            if (pedido.aprovadoPor.signatureUrl) {
                try {
                    const signatureImg = await new Promise((resolve, reject) => {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.onload = () => resolve(img);
                        img.onerror = () => reject(new Error('Falha ao carregar assinatura'));
                        img.src = config.urlArquivo(pedido.aprovadoPor.signatureUrl);
                    });

                    // Calcular dimensões proporcionais (max 100mm largura x 45mm altura)
                    const maxW = 100;
                    const maxH = 45;
                    const ratio = Math.min(maxW / signatureImg.width, maxH / signatureImg.height);
                    const imgW = signatureImg.width * ratio;
                    const imgH = signatureImg.height * ratio;

                    doc.addImage(signatureImg, 'PNG', approvalCenterX - imgW / 2, posY, imgW, imgH);
                    posY += imgH + 4;
                } catch (err) {
                    console.warn('Não foi possível carregar a imagem da assinatura:', err);
                    posY += 5;
                }
            }

            // Linha de assinatura
            doc.setDrawColor(100, 100, 100);
            doc.setLineWidth(0.3);
            doc.line(approvalCenterX - 45, posY, approvalCenterX + 45, posY);
            posY += 5;

            // Nome do aprovador
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.setFont(undefined, 'bold');
            doc.text(pedido.aprovadoPor.nome || 'N/A', approvalCenterX, posY, { align: 'center' });
            posY += 4;

            // Data de aprovação
            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100, 100, 100);
            const dataAprovacaoFormatada = pedido.dataAprovacao
                ? new Date(pedido.dataAprovacao).toLocaleString('pt-BR')
                : '';
            doc.text(`Aprovado em: ${dataAprovacaoFormatada}`, approvalCenterX, posY, { align: 'center' });
            posY += 8;
        }

        // ============================================
        // RODAPÉ
        // ============================================
        const pageCount = doc.internal.getNumberOfPages();
        const dataEmissaoPDF = new Date().toLocaleString('pt-BR');
        
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            
            const rodapeY = pageHeight - 15;
            
            // Linha superior
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.line(14, rodapeY, pageWidth - 14, rodapeY);
            
            // Informações
            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.setFont(undefined, 'normal');
            doc.text(`Emitido por: ${auth.user?.name || "Sistema"}`, 14, rodapeY + 4);
            doc.text(`Data/Hora de Impressão: ${dataEmissaoPDF}`, 14, rodapeY + 8);
            
            // Logo centralizado
            if (logoEmpresa) {
                const logoW = 22;
                const logoH = 7;
                doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoW) / 2, rodapeY + 2, logoW, logoH);
            }
            
            // Número da página
            doc.setFont(undefined, 'bold');
            doc.text(`Página ${i} de ${pageCount}`, pageWidth - 14, rodapeY + 8, { align: 'right' });
        }

        // Salvar
        const nomeArquivo = `pedido_compra_${pedido.numeroOC || pedido.numero || id}_${new Date().getTime()}.pdf`;
        doc.save(nomeArquivo);
    };

    // Função auxiliar para converter hex em RGB
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
        ] : [150, 150, 150];
    };

    const handleSuccessClose = () => {
        setShowSuccessDialog(false);
        if (successMessage.includes("Pedido finalizado")) {
            navigate("/pedidos-compra");
        }
    };

    if (loading) {
        return (
            <Template>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <div style={{ textAlign: "center" }}>
                        <div style={{
                            border: "4px solid #f3f3f3",
                            borderTop: "4px solid #3B82F6",
                            borderRadius: "50%",
                            width: "40px",
                            height: "40px",
                            animation: "spin 1s linear infinite",
                            margin: "0 auto"
                        }}></div>
                        <p style={{ marginTop: "10px", color: "#6B7280" }}>Carregando...</p>
                    </div>
                </div>
            </Template>
        );
    }

    if (!pedido) {
        return (
            <Template>
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <h2 style={{ color: "#EF4444", marginBottom: "10px" }}>Pedido não encontrado</h2>
                    <button
                        onClick={() => navigate("/pedidos-compra")}
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#3B82F6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600"
                        }}
                    >
                        Voltar para Lista
                    </button>
                </div>
            </Template>
        );
    }

    return (
        <Template>
            <div className="pedido-detalhes-page" style={{ padding: "20px" }}>
                {/* Cabeçalho */}
                <div className="pedido-header" style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "30px"
                }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <h1 className="pedido-titulo" style={{ fontSize: "28px", fontWeight: "700", color: "#1F2937", marginBottom: "8px" }}>
                            Pedido de Compra - {pedido.numero}
                        </h1>
                        <div className="pedido-header-meta" style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                            <span style={{
                                padding: "6px 12px",
                                backgroundColor: getStatusColor(pedido.status),
                                color: "white",
                                borderRadius: "6px",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}>
                                {getStatusLabel(pedido.status)}
                            </span>
                            <span style={{ fontSize: "14px", color: "#6B7280" }}>
                                Emitido em {formatDateWithoutHour(pedido.dataEmissao)}
                            </span>
                        </div>
                    </div>

                    <div className="pedido-header-actions" style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
                        <button
                            onClick={() => navigate("/pedidos-compra")}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#F3F4F6",
                                color: "#374151",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Voltar
                        </button>

                        <button
                            onClick={gerarPDF}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#10B981",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}
                        >
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M8.5 6.5a.5.5 0 0 0-1 0v3.793L6.354 9.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 0 0-.708-.708L8.5 10.293V6.5z"/>
                                <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                            </svg>
                            Imprimir PDF
                        </button>

                        {podeEditar && (
                            <button
                                onClick={() => navigate(`/pedidos-compra/${id}/editar`)}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: isStatus(pedido.status, "RASCUNHO", "Rascunho", "EM_ORCAMENTO", "Em Orçamento") ? "#3B82F6" : "#6B7280",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "600"
                                }}
                            >
                                {isStatus(pedido.status, "RASCUNHO", "Rascunho", "EM_ORCAMENTO", "Em Orçamento") ? "Editar" : "Gerenciar Boletos/Anexos"}
                            </button>
                        )}

                        {podeEditarQualquerStatus && !isStatus(pedido.status, "Cancelada") && (
                            <button
                                onClick={handleCancelar}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#DC2626",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "600"
                                }}
                            >
                                🚫 Cancelar Pedido
                            </button>
                        )}
                    </div>
                </div>

                {/* Card para Rascunho - Enviar para Orçamento ou Aprovação */}
                {isStatus(pedido.status, "RASCUNHO", "Rascunho") && podeEnviarOrcamento && (
                    <div className="pedido-status-card" style={{
                        backgroundColor: "#E0E7FF",
                        border: "1px solid #818CF8",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "24px"
                    }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#3730A3" }}>
                            Pedido em Rascunho
                        </h3>
                        <p style={{ fontSize: "14px", color: "#4338CA", marginBottom: "16px" }}>
                            Este pedido está em modo rascunho. Envie para orçamento para iniciar a fase de cotação, ou envie diretamente para aprovação.
                        </p>
                        <div className="pedido-status-actions" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button
                                onClick={handleEnviarParaOrcamento}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#F97316",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "600"
                                }}
                            >
                                Enviar para Orçamento
                            </button>
                            <button
                                onClick={handleEnviarParaAprovacao}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#3B82F6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "600"
                                }}
                            >
                                Enviar para Aprovação
                            </button>
                            {podeEditar && (
                                <button
                                    onClick={() => navigate(`/pedidos-compra/${id}/editar`)}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#6B7280",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "600"
                                    }}
                                >
                                    Editar Pedido
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Card para Em Orçamento - Alerta para prosseguir */}
                {isStatus(pedido.status, "EM_ORCAMENTO", "Em Orçamento") && (
                    <div className="pedido-status-card" style={{
                        backgroundColor: "#FFF7ED",
                        border: "2px solid #FB923C",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "24px"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                            <span style={{ fontSize: "24px" }}>⚠️</span>
                            <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#9A3412", margin: 0 }}>
                                Pedido em Orçamento
                            </h3>
                        </div>
                        <p style={{ fontSize: "14px", color: "#C2410C", marginBottom: "16px" }}>
                            Este pedido está em fase de orçamento. Após concluir a cotação e análise, envie para aprovação para prosseguir com o fluxo.
                        </p>
                        <div className="pedido-status-actions" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {podeEnviarAprovacao && (
                                <button
                                    onClick={handleEnviarParaAprovacao}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#3B82F6",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "600"
                                    }}
                                >
                                    Enviar para Aprovação
                                </button>
                            )}
                            {podeEditar && (
                                <button
                                    onClick={() => navigate(`/pedidos-compra/${id}/editar`)}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#6B7280",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "600"
                                    }}
                                >
                                    Editar Pedido
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Ações de Status */}
                {isStatus(pedido.status, "AGUARDANDO_APROVACAO", "Aguardando Aprovação") && (podeAprovar || podeRejeitar) && (
                    <div className="pedido-status-card" style={{
                        backgroundColor: "#FEF3C7",
                        border: "1px solid #FCD34D",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "24px"
                    }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#92400E" }}>
                            Aprovação Pendente
                        </h3>
                        <p style={{ fontSize: "14px", color: "#78350F", marginBottom: "16px" }}>
                            Este pedido aguarda aprovação. 
                            {podeAprovar && podeRejeitar && " Você deseja aprovar ou rejeitar?"}
                            {podeAprovar && !podeRejeitar && " Você pode aprovar este pedido."}
                            {!podeAprovar && podeRejeitar && " Você pode rejeitar este pedido."}
                        </p>
                        <div className="pedido-status-actions" style={{ display: "flex", gap: "10px" }}>
                            {podeAprovar && (
                                <button
                                    onClick={handleAprovar}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#10B981",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "600"
                                    }}
                                >
                                    Aprovar
                                </button>
                            )}
                            {podeRejeitar && (
                                <button
                                    onClick={handleRejeitar}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#EF4444",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "600"
                                    }}
                                >
                                    Rejeitar
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {isStatus(pedido.status, "APROVADO", "Aprovado") && podeMudarStatus && (
                    <div className="pedido-status-card" style={{
                        backgroundColor: "#DBEAFE",
                        border: "1px solid #93C5FD",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "24px"
                    }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#1E40AF" }}>
                            Pedido Aprovado
                        </h3>
                        <p style={{ fontSize: "14px", color: "#1E3A8A", marginBottom: "16px" }}>
                            Marque como "Em Andamento" quando iniciar o processamento.
                        </p>
                        <button
                            onClick={handleMarcarEmAndamento}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#3B82F6",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Marcar como Em Andamento
                        </button>
                    </div>
                )}

                {isStatus(pedido.status, "EM_ANDAMENTO", "Em andamento") && podeMudarStatus && podeReceber && (
                    <div className="pedido-status-card" style={{
                        backgroundColor: "#DDD6FE",
                        border: "1px solid #C4B5FD",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "24px"
                    }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#5B21B6" }}>
                            Pedido Em Andamento
                        </h3>
                        <p style={{ fontSize: "14px", color: "#6B21A8", marginBottom: "16px" }}>
                            Marque como "Recebido" quando os produtos forem entregues.
                        </p>
                        <button
                            onClick={handleMarcarRecebido}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#8B5CF6",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Marcar como Recebido
                        </button>
                    </div>
                )}

                {isStatus(pedido.status, "RECEBIDO", "Recebida") && podeMudarStatus && podeConcluir && (
                    <div className="pedido-status-card" style={{
                        backgroundColor: "#E0E7FF",
                        border: "1px solid #C7D2FE",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "24px"
                    }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#4338CA" }}>
                            Pedido Recebido
                        </h3>
                        <p style={{ fontSize: "14px", color: "#4338CA", marginBottom: "16px" }}>
                            Produtos recebidos. Finalize o pedido para concluir o processo.
                        </p>
                        <button
                            onClick={handleFinalizar}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#6366F1",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Finalizar Pedido
                        </button>
                    </div>
                )}

                {/* Informações do Pedido */}
                <div className="pedido-info-grid" style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "20px",
                    marginBottom: "24px"
                }}>
                    {/* Dados Gerais */}
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#1F2937" }}>
                            Dados Gerais
                        </h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Número OC
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                        {pedido.numeroOC || pedido.numero}
                                    </span>
                                    {podeAlterarNumeroOC && (
                                        <button
                                            type="button"
                                            onClick={handleAlterarNumeroOC}
                                            title="Alterar número do pedido (renumeração)"
                                            style={{
                                                padding: "4px 10px",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                                border: "1px solid #D1D5DB",
                                                backgroundColor: "white",
                                                color: "#374151",
                                                borderRadius: "6px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            Alterar
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Data de Emissão
                                </span>
                                <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                    {formatDateWithoutHour(pedido.dataEmissao)}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Comprador
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.comprador?.name || pedido.comprador?.nome || "Não informado"}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Valor Total
                                </span>
                                <span style={{ fontSize: "18px", fontWeight: "700", color: "#10B981" }}>
                                    {formatCurrencyFromDb(pedido.valorTotal)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Fornecedor */}
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#1F2937" }}>
                            Fornecedor
                        </h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Nome
                                </span>
                                <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                    {pedido.fornecedor?.nome || pedido.fornecedor?.nomeFornecedor || "N/A"}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    CPF/CNPJ
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.fornecedor?.cpfCnpj || "Não informado"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Nota Fiscal Vinculada */}
                    {pedido.notaFiscal && (
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "20px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            border: "1px solid #E0E7FF"
                        }}>
                            <h2 style={{ 
                                fontSize: "16px", 
                                fontWeight: "600", 
                                marginBottom: "16px", 
                                color: "#1F2937",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <span style={{ fontSize: "20px" }}>📄</span>
                                Nota Fiscal Vinculada
                            </h2>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                <div>
                                    <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                        Número/Série
                                    </span>
                                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                        NF {pedido.notaFiscal.numero}{pedido.notaFiscal.serie ? `/${pedido.notaFiscal.serie}` : ''}
                                    </span>
                                </div>
                                <div>
                                    <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                        Fornecedor
                                    </span>
                                    <span style={{ fontSize: "14px", color: "#111827" }}>
                                        {pedido.notaFiscal.fornecedor?.nomeFornecedor || "N/A"}
                                    </span>
                                </div>
                                <div className="pedido-nf-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                    <div>
                                        <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                            Data de Emissão
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#111827" }}>
                                            {pedido.notaFiscal.dataEmissao 
                                                ? new Date(pedido.notaFiscal.dataEmissao).toLocaleDateString('pt-BR') 
                                                : "N/A"}
                                        </span>
                                    </div>
                                    {pedido.notaFiscal.valorNfe && (
                                        <div>
                                            <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                                Valor da NF
                                            </span>
                                            <span style={{ fontSize: "14px", fontWeight: "600", color: "#059669" }}>
                                                R$ {parseFloat(pedido.notaFiscal.valorNfe).toLocaleString('pt-BR', { 
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2 
                                                })}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {pedido.notaFiscal.chave && (
                                    <div>
                                        <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                            Chave de Acesso
                                        </span>
                                        <span className="pedido-nf-chave" style={{ 
                                            fontSize: "11px", 
                                            color: "#6B7280",
                                            fontFamily: "monospace",
                                            backgroundColor: "#F3F4F6",
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            display: "inline-block"
                                        }}>
                                            {pedido.notaFiscal.chave}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Botão de desvincular */}
                                {(podeEditar || podeMudarStatus) && (
                                    <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #E5E7EB" }}>
                                        <button
                                            onClick={desvincularNotaFiscal}
                                            style={{
                                                padding: "8px 16px",
                                                backgroundColor: "#EF4444",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "6px",
                                                fontSize: "14px",
                                                cursor: "pointer",
                                                fontWeight: "500"
                                            }}
                                        >
                                            Desvincular Nota Fiscal
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Campo para vincular/alterar nota quando não há nota vinculada */}
                    {!pedido.notaFiscal && (podeEditar || podeMudarStatus) && (
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "20px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            border: "1px solid #E0E7FF"
                        }}>
                            <h2 style={{ 
                                fontSize: "16px", 
                                fontWeight: "600", 
                                marginBottom: "12px", 
                                color: "#1F2937",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <span style={{ fontSize: "20px" }}>📄</span>
                                Vincular Nota Fiscal
                            </h2>
                            
                            <div style={{ position: "relative" }}>
                                <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "8px", color: "#374151" }}>
                                    Chave da Nota Fiscal
                                </label>
                                <input
                                    type="text"
                                    value={numeroNotaFiscal}
                                    onChange={(e) => {
                                        setNumeroNotaFiscal(e.target.value);
                                        buscarNotasFiscaisPorChave(e.target.value);
                                    }}
                                    onFocus={() => {
                                        if (notasFiscaisSugestoes.length > 0) {
                                            setShowSugestoesNota(true);
                                        }
                                    }}
                                    placeholder="Digite a chave da nota fiscal..."
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontFamily: "monospace"
                                    }}
                                />
                                <p style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                                    Digite pelo menos 5 caracteres da chave para buscar
                                </p>
                                
                                {/* Sugestões de notas fiscais */}
                                {showSugestoesNota && notasFiscaisSugestoes.length > 0 && (
                                    <div style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                        right: 0,
                                        backgroundColor: "white",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "8px",
                                        marginTop: "4px",
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                        zIndex: 10
                                    }}>
                                        {notasFiscaisSugestoes.map((nota) => (
                                            <div
                                                key={nota.id}
                                                onClick={() => selecionarNotaFiscal(nota)}
                                                style={{
                                                    padding: "10px 12px",
                                                    cursor: "pointer",
                                                    borderBottom: "1px solid #F3F4F6",
                                                    backgroundColor: notaFiscalSelecionada?.id === nota.id ? "#EFF6FF" : "white"
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "#F9FAFB"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = notaFiscalSelecionada?.id === nota.id ? "#EFF6FF" : "white"}
                                            >
                                                <div style={{ fontWeight: "600", fontSize: "14px" }}>
                                                    NF {nota.numero}{nota.serie ? `/${nota.serie}` : ''}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#6B7280" }}>
                                                    {nota.fornecedor?.nomeFornecedor || "Fornecedor não informado"}
                                                </div>
                                                {nota.valorNfe && (
                                                    <div style={{ fontSize: "12px", color: "#059669", marginTop: "2px" }}>
                                                        R$ {parseFloat(nota.valorNfe).toLocaleString('pt-BR', { 
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2 
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            {notaFiscalSelecionada && (
                                <button
                                    onClick={vincularNotaFiscal}
                                    style={{
                                        marginTop: "12px",
                                        padding: "10px 20px",
                                        backgroundColor: "#3B82F6",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Confirmar Vinculação
                                </button>
                            )}
                        </div>
                    )}

                    {/* Detalhes */}
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#1F2937" }}>
                            Detalhes
                        </h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Obra
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.obra?.nome || pedido.obra?.nomeProjeto || pedido.nomeObra || "Não informado"}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Escopo
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.escopo || "Não informado"}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Prazo de Entrega
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.prazoEntrega ? `${pedido.prazoEntrega} dias` : 'Não informado'}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Forma de Pagamento
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.formaPagamento || 'Não informado'}
                                    {pedido.quantidadeParcelas && ` - ${pedido.quantidadeParcelas}x`}
                                </span>
                            </div>
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "4px" }}>
                                    Condições de Pagamento
                                </span>
                                <span style={{ fontSize: "14px", color: "#111827" }}>
                                    {pedido.condicoesPagamento || "Não informado"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Detalhes de Pagamento - Novo componente */}
                <DetalhesPagamentoView pedido={pedido} />

                {/* Comentários de Aprovação/Rejeição */}
                {(pedido.comentarioAprovacao || pedido.comentarioRejeicao) && (
                    <div className="pedido-section-card" style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#1F2937" }}>
                            Comentários
                        </h2>
                        {pedido.comentarioAprovacao && (
                            <div style={{ marginBottom: pedido.comentarioRejeicao ? "16px" : "0" }}>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "8px" }}>
                                    Comentário de Aprovação:
                                </span>
                                <div style={{ 
                                    padding: "12px", 
                                    backgroundColor: "#ECFDF5", 
                                    borderLeft: "4px solid #10B981",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    color: "#111827"
                                }}>
                                    {pedido.comentarioAprovacao}
                                </div>
                            </div>
                        )}
                        {pedido.comentarioRejeicao && (
                            <div>
                                <span style={{ fontSize: "12px", color: "#6B7280", display: "block", marginBottom: "8px" }}>
                                    Comentário de Rejeição:
                                </span>
                                <div style={{ 
                                    padding: "12px", 
                                    backgroundColor: "#FEF2F2", 
                                    borderLeft: "4px solid #EF4444",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    color: "#111827"
                                }}>
                                    {pedido.comentarioRejeicao}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Itens do Pedido */}
                <div className="pedido-section-card" style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "24px",
                    marginBottom: "24px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                }}>
                    <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px", color: "#1F2937" }}>
                        Itens do Pedido
                    </h2>

                    <div style={{ overflowX: "auto" }}>
                        <table className="pedido-itens-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                                    <th style={{ padding: "12px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                        Descrição
                                    </th>
                                    <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                        Quantidade
                                    </th>
                                    <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                        Unidade
                                    </th>
                                    <th style={{ padding: "12px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                        Preço Unit.
                                    </th>
                                    <th style={{ padding: "12px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                        Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pedido.itens && pedido.itens.length > 0 ? (
                                    pedido.itens.map((item, index) => (
                                        <tr key={item.id || index} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                            <td data-label="Descrição" style={{ padding: "12px", fontSize: "14px", color: "#111827" }}>
                                                {item.descricao}
                                            </td>
                                            <td data-label="Quantidade" style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: "#111827" }}>
                                                {item.quantidade}
                                            </td>
                                            <td data-label="Unidade" style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: "#111827" }}>
                                                {item.unidade}
                                            </td>
                                            <td data-label="Preço Unit." style={{ padding: "12px", textAlign: "right", fontSize: "14px", color: "#111827" }}>
                                                {formatCurrencyFromDb(item.precoUnitario)}
                                            </td>
                                            <td data-label="Total" style={{ padding: "12px", textAlign: "right", fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                                {formatCurrencyFromDb(item.valorTotal)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: "20px", textAlign: "center", color: "#6B7280" }}>
                                            Nenhum item encontrado
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: "#F9FAFB", fontWeight: "bold" }}>
                                    <td colSpan="4" style={{ padding: "12px", textAlign: "right", fontSize: "16px", color: "#1F2937" }}>
                                        Total do Pedido:
                                    </td>
                                    <td style={{ padding: "12px", textAlign: "right", fontSize: "18px", fontWeight: "700", color: "#10B981" }}>
                                        {formatCurrencyFromDb(pedido.valorTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Observações */}
                <div className="pedido-section-card" style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "24px",
                    marginBottom: "24px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                }}>
                    <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "12px", color: "#1F2937" }}>
                        Observações
                    </h2>
                    <p style={{ fontSize: "14px", color: "#4B5563", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                        {pedido.observacoes || pedido.observacao || "Não informado"}
                    </p>
                </div>

                {/* Retenções de Impostos */}
                {pedido.retencoes && pedido.retencoes.length > 0 && (
                    <div className="pedido-section-card" style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "24px",
                        marginBottom: "24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px", color: "#1F2937" }}>
                            💰 Impostos Retidos
                        </h2>

                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                                        <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>Tipo</th>
                                        <th style={{ padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>Alíquota</th>
                                        <th style={{ padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>Base Cálculo</th>
                                        <th style={{ padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>Valor</th>
                                        <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>Observação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pedido.retencoes.map(r => (
                                        <tr key={r.id} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                            <td style={{ padding: "10px" }}>{r.tipoImposto?.nome || '-'}</td>
                                            <td style={{ padding: "10px", textAlign: "right" }}>{r.aliquota != null ? `${r.aliquota}%` : '-'}</td>
                                            <td style={{ padding: "10px", textAlign: "right" }}>{r.baseCalculo != null ? formatCurrencyFromDb(r.baseCalculo) : '-'}</td>
                                            <td style={{ padding: "10px", textAlign: "right", color: "#DC2626", fontWeight: "600" }}>{formatCurrencyFromDb(r.valor)}</td>
                                            <td style={{ padding: "10px" }}>{r.observacao || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{
                            marginTop: "16px",
                            padding: "12px 16px",
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "16px",
                            flexWrap: "wrap"
                        }}>
                            <div>
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>Total de Retenções</div>
                                <div style={{ fontSize: "16px", fontWeight: "600", color: "#DC2626" }}>
                                    − {formatCurrencyFromDb(pedido.valorTotalRetencoes || 0)}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>Valor Líquido ao Fornecedor</div>
                                <div style={{ fontSize: "20px", fontWeight: "700", color: "#059669" }}>
                                    {formatCurrencyFromDb(pedido.valorLiquidoFornecedor || 0)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Anexos */}
                {pedido.anexos && pedido.anexos.length > 0 && (
                    <div className="pedido-section-card" style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "24px",
                        marginBottom: "24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px", color: "#1F2937" }}>
                            Anexos e Documentos
                        </h2>

                        <div style={{ overflowX: "auto" }}>
                            <table className="pedido-anexos-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                                        <th style={{ padding: "12px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Tipo de Documento
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Arquivo
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Data de Envio
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Data de Vencimento
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Nº Parcela
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pedido.anexos.map((anexo, index) => {
                                        const isBoleto = anexo.tipo?.nome?.toLowerCase().includes('boleto');
                                        return (
                                            <tr key={anexo.id || index} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                                <td data-label="Tipo" style={{ padding: "12px", fontSize: "14px", color: "#111827" }}>
                                                    {isBoleto && <span style={{ marginRight: "6px" }}>📄</span>}
                                                    {anexo.tipo?.nome || 'Documento'}
                                                </td>
                                                <td data-label="Arquivo" style={{ padding: "12px", fontSize: "14px", color: "#6B7280" }}>
                                                    {anexo.arquivo?.split('/').pop() || 'Arquivo'}
                                                </td>
                                                <td data-label="Data Envio" style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: "#6B7280" }}>
                                                    {formatDateWithoutHour(anexo.dataEnvio)}
                                                </td>
                                                <td data-label="Vencimento" style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: isBoleto ? "#111827" : "#9CA3AF", fontWeight: isBoleto ? "500" : "normal" }}>
                                                    {anexo.dataVencimento ? (
                                                        <span style={{ 
                                                            display: "inline-block", 
                                                            padding: "4px 8px", 
                                                            backgroundColor: "#FEF3C7", 
                                                            color: "#92400E", 
                                                            borderRadius: "6px",
                                                            fontSize: "13px"
                                                        }}>
                                                            {formatDateWithoutHour(anexo.dataVencimento)}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: "#D1D5DB" }}>-</span>
                                                    )}
                                                </td>
                                                <td data-label="Nº Parcela" style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: isBoleto ? "#111827" : "#9CA3AF", fontWeight: isBoleto ? "500" : "normal" }}>
                                                    {anexo.numeroParcela ? (
                                                        <span style={{ 
                                                            display: "inline-block", 
                                                            padding: "4px 10px", 
                                                            backgroundColor: "#DBEAFE", 
                                                            color: "#1E40AF", 
                                                            borderRadius: "6px",
                                                            fontSize: "13px",
                                                            fontWeight: "600"
                                                        }}>
                                                            {anexo.numeroParcela}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: "#D1D5DB" }}>-</span>
                                                    )}
                                                </td>
                                                <td data-label="Ações" style={{ padding: "12px", textAlign: "center" }}>
                                                    <a
                                                        href={config.urlArquivo(anexo.arquivo)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            display: "inline-block",
                                                            padding: "6px 12px",
                                                            backgroundColor: "#3B82F6",
                                                            color: "white",
                                                            borderRadius: "6px",
                                                            fontSize: "13px",
                                                            textDecoration: "none",
                                                            fontWeight: "500"
                                                        }}
                                                    >
                                                        Baixar
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Histórico */}
                {historico && historico.length > 0 && (
                    <div className="pedido-section-card" style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}>
                        <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px", color: "#1F2937" }}>
                            Histórico de Alterações
                        </h2>

                        <div style={{ position: "relative", paddingLeft: "30px" }}>
                            {historico.map((item, index) => (
                                <div key={item.id || index} style={{ position: "relative", paddingBottom: "20px" }}>
                                    <div style={{
                                        position: "absolute",
                                        left: "-22px",
                                        top: "0",
                                        width: "12px",
                                        height: "12px",
                                        borderRadius: "50%",
                                        backgroundColor: "#3B82F6",
                                        border: "3px solid white",
                                        boxShadow: "0 0 0 2px #3B82F6"
                                    }}></div>

                                    {index < historico.length - 1 && (
                                        <div style={{
                                            position: "absolute",
                                            left: "-17px",
                                            top: "12px",
                                            width: "2px",
                                            height: "calc(100% - 12px)",
                                            backgroundColor: "#E5E7EB"
                                        }}></div>
                                    )}

                                    <div>
                                        <div className="pedido-historico-item-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "4px" }}>
                                            <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                                {traduzirAcao(item.acao, item.statusAnterior, item.statusNovo)}
                                            </span>
                                            <span style={{ fontSize: "12px", color: "#6B7280" }}>
                                                {formatDateWithHour(item.createdAt || item.dataHora)}
                                            </span>
                                        </div>
                                        {item.comentario && (
                                            <p style={{ fontSize: "14px", color: "#6B7280", marginTop: "4px" }}>
                                                {item.comentario}
                                            </p>
                                        )}
                                        {item.usuario && (
                                            <p style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>
                                                Por: {typeof item.usuario === 'string' ? item.usuario : item.usuario.nome}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Modais */}
                <ConfirmationModal
                    isVisible={showConfirmEnviarOrcamento}
                    onCancel={() => setShowConfirmEnviarOrcamento(false)}
                    onConfirm={confirmarEnvioOrcamento}
                    message="Deseja enviar este pedido para orçamento? O pedido ficará com status 'Em Orçamento' e poderá ser editado. Apenas usuários com permissão poderão visualizá-lo."
                    textButtonConfirm="Enviar para Orçamento"
                />

                <ConfirmationModal
                    isVisible={showConfirmEnviarAprovacao}
                    onCancel={() => setShowConfirmEnviarAprovacao(false)}
                    onConfirm={confirmarEnvioAprovacao}
                    message="Deseja enviar este pedido para aprovação? Após o envio, o pedido não poderá mais ser editado até que seja aprovado ou rejeitado."
                    textButtonConfirm="Enviar para Aprovação"
                />

                <ConfirmationModal
                    isVisible={showConfirmAprovar}
                    onCancel={() => setShowConfirmAprovar(false)}
                    onConfirm={confirmarAprovacao}
                    message="Deseja realmente aprovar este pedido de compra?"
                    textButtonConfirm="Aprovar"
                />

                <ConfirmationModal
                    isVisible={showConfirmRejeitar}
                    onCancel={() => {
                        setShowConfirmRejeitar(false);
                        setMotivoRejeicao("");
                    }}
                    onConfirm={confirmarRejeicao}
                    message={
                        <div>
                            <p style={{ marginBottom: "12px" }}>Deseja realmente rejeitar este pedido?</p>
                            <textarea
                                placeholder="Informe o motivo da rejeição"
                                value={motivoRejeicao}
                                onChange={(e) => setMotivoRejeicao(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    minHeight: "80px"
                                }}
                            />
                        </div>
                    }
                    textButtonConfirm="Rejeitar"
                />

                <ConfirmationModal
                    isVisible={showConfirmAndamento}
                    onCancel={() => setShowConfirmAndamento(false)}
                    onConfirm={confirmarAndamento}
                    message="Marcar este pedido como Em Andamento?"
                    textButtonConfirm="Confirmar"
                />

                <ConfirmationModal
                    isVisible={showConfirmRecebido}
                    onCancel={() => setShowConfirmRecebido(false)}
                    onConfirm={confirmarRecebido}
                    message="Confirmar o recebimento dos produtos deste pedido?"
                    textButtonConfirm="Confirmar"
                />

                <ConfirmationModal
                    isVisible={showConfirmFinalizar}
                    onCancel={() => setShowConfirmFinalizar(false)}
                    onConfirm={confirmarFinalizacao}
                    message="Deseja finalizar este pedido? Esta ação não pode ser desfeita."
                    textButtonConfirm="Finalizar"
                />

                <SuccessModal
                    isOpen={showSuccessDialog}
                    onClose={handleSuccessClose}
                    message={successMessage}
                />

                {/* Modal: Cancelar Pedido */}
                {showCancelarDialog && (
                    <div style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "24px",
                            width: "min(520px, 90vw)",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
                        }}>
                            <h3 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px", color: "#991B1B" }}>
                                🚫 Cancelar Pedido de Compra
                            </h3>
                            <p style={{ fontSize: "14px", color: "#4B5563", marginBottom: "16px" }}>
                                Esta ação muda o status para <strong>Cancelada</strong> e fica registrada no histórico de auditoria.
                                Informe o <strong>motivo</strong> do cancelamento.
                            </p>
                            <textarea
                                value={motivoCancelamento}
                                onChange={(e) => setMotivoCancelamento(e.target.value)}
                                placeholder="Motivo do cancelamento (obrigatório)"
                                rows={4}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    resize: "vertical",
                                    fontFamily: "inherit"
                                }}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                                <button
                                    type="button"
                                    onClick={() => { setShowCancelarDialog(false); setMotivoCancelamento(""); }}
                                    disabled={cancelando}
                                    style={{
                                        padding: "10px 18px",
                                        border: "1px solid #D1D5DB",
                                        backgroundColor: "white",
                                        borderRadius: "6px",
                                        cursor: cancelando ? "not-allowed" : "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmarCancelamento}
                                    disabled={cancelando || !motivoCancelamento.trim()}
                                    style={{
                                        padding: "10px 18px",
                                        border: "none",
                                        backgroundColor: !motivoCancelamento.trim() ? "#FCA5A5" : "#DC2626",
                                        color: "white",
                                        borderRadius: "6px",
                                        cursor: cancelando || !motivoCancelamento.trim() ? "not-allowed" : "pointer",
                                        fontWeight: "600"
                                    }}
                                >
                                    {cancelando ? "Cancelando..." : "Confirmar Cancelamento"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal: Alterar Número OC */}
                {showAlterarNumeroDialog && (
                    <div style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "24px",
                            width: "min(520px, 90vw)",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
                        }}>
                            <h3 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px", color: "#1F2937" }}>
                                Alterar Número do Pedido
                            </h3>
                            <p style={{ fontSize: "13px", color: "#4B5563", marginBottom: "16px", lineHeight: 1.5 }}>
                                Atual: <strong>{pedido?.numeroOC || pedido?.numero || "—"}</strong>. Esta operação fica
                                registrada na auditoria e no histórico do pedido. Informe o novo número e o motivo.
                            </p>

                            <label style={{ fontSize: "12px", color: "#374151", fontWeight: "600", display: "block", marginBottom: "4px" }}>
                                Novo número
                            </label>
                            <input
                                type="text"
                                value={novoNumeroOC}
                                onChange={(e) => setNovoNumeroOC(e.target.value)}
                                placeholder="Ex.: OC-2026-0764"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    fontFamily: "inherit",
                                    marginBottom: "12px"
                                }}
                            />

                            <label style={{ fontSize: "12px", color: "#374151", fontWeight: "600", display: "block", marginBottom: "4px" }}>
                                Motivo da alteração
                            </label>
                            <textarea
                                value={motivoAlteracaoNumero}
                                onChange={(e) => setMotivoAlteracaoNumero(e.target.value)}
                                placeholder="Justificativa da renumeração (obrigatório)"
                                rows={3}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    resize: "vertical",
                                    fontFamily: "inherit"
                                }}
                            />

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAlterarNumeroDialog(false);
                                        setNovoNumeroOC("");
                                        setMotivoAlteracaoNumero("");
                                    }}
                                    disabled={alterandoNumero}
                                    style={{
                                        padding: "10px 18px",
                                        border: "1px solid #D1D5DB",
                                        backgroundColor: "white",
                                        borderRadius: "6px",
                                        cursor: alterandoNumero ? "not-allowed" : "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmarAlteracaoNumero}
                                    disabled={
                                        alterandoNumero ||
                                        !novoNumeroOC.trim() ||
                                        !motivoAlteracaoNumero.trim() ||
                                        novoNumeroOC.trim() === (pedido?.numeroOC || "")
                                    }
                                    style={{
                                        padding: "10px 18px",
                                        border: "none",
                                        backgroundColor:
                                            !novoNumeroOC.trim() ||
                                            !motivoAlteracaoNumero.trim() ||
                                            novoNumeroOC.trim() === (pedido?.numeroOC || "")
                                                ? "#93C5FD"
                                                : "#2563EB",
                                        color: "white",
                                        borderRadius: "6px",
                                        cursor:
                                            alterandoNumero ||
                                            !novoNumeroOC.trim() ||
                                            !motivoAlteracaoNumero.trim() ||
                                            novoNumeroOC.trim() === (pedido?.numeroOC || "")
                                                ? "not-allowed"
                                                : "pointer",
                                        fontWeight: "600"
                                    }}
                                >
                                    {alterandoNumero ? "Salvando..." : "Confirmar Alteração"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    @media (max-width: 640px) {
                        .pedido-detalhes-page {
                            padding: 4px !important;
                            overflow-x: hidden !important;
                            max-width: 100vw !important;
                        }

                        .pedido-header {
                            flex-direction: column !important;
                            gap: 12px !important;
                            margin-bottom: 16px !important;
                        }

                        .pedido-titulo {
                            font-size: 18px !important;
                            word-break: break-word !important;
                        }

                        .pedido-header-meta {
                            flex-wrap: wrap !important;
                            gap: 8px !important;
                        }

                        .pedido-header-actions {
                            width: 100% !important;
                            flex-wrap: wrap !important;
                            gap: 8px !important;
                        }

                        .pedido-header-actions button {
                            flex: 1 1 auto !important;
                            min-width: 0 !important;
                            padding: 10px 12px !important;
                            font-size: 13px !important;
                            white-space: nowrap !important;
                        }

                        .pedido-info-grid {
                            grid-template-columns: 1fr !important;
                            gap: 12px !important;
                        }

                        .pedido-status-card {
                            padding: 12px !important;
                            margin-bottom: 16px !important;
                        }

                        .pedido-status-card .pedido-status-actions {
                            flex-wrap: wrap !important;
                            gap: 8px !important;
                        }

                        .pedido-status-card .pedido-status-actions button {
                            flex: 1 1 auto !important;
                            min-width: 120px !important;
                        }

                        .pedido-section-card {
                            padding: 16px !important;
                            margin-bottom: 16px !important;
                            overflow: hidden !important;
                            max-width: 100% !important;
                            box-sizing: border-box !important;
                        }

                        .pedido-section-card h2 {
                            font-size: 16px !important;
                        }

                        /* Itens table - mobile cards */
                        .pedido-itens-table thead {
                            display: none !important;
                        }

                        .pedido-itens-table tbody tr {
                            display: block !important;
                            background: #F9FAFB !important;
                            border: 1px solid #E5E7EB !important;
                            border-radius: 10px !important;
                            padding: 14px !important;
                            margin-bottom: 10px !important;
                            overflow: hidden !important;
                            max-width: 100% !important;
                            box-sizing: border-box !important;
                        }

                        .pedido-itens-table tbody td {
                            display: block !important;
                            padding: 0 !important;
                            text-align: left !important;
                            border: none !important;
                        }

                        /* Descrição - destaque principal */
                        .pedido-itens-table tbody td[data-label="Descrição"] {
                            font-size: 15px !important;
                            font-weight: 600 !important;
                            color: #111827 !important;
                            margin-bottom: 10px !important;
                            padding-bottom: 8px !important;
                            border-bottom: 1px solid #E5E7EB !important;
                        }

                        .pedido-itens-table tbody td[data-label="Descrição"]::before {
                            display: none !important;
                        }

                        /* Campos numéricos em grid 2x2 */
                        .pedido-itens-table tbody td[data-label="Quantidade"],
                        .pedido-itens-table tbody td[data-label="Unidade"],
                        .pedido-itens-table tbody td[data-label="Preço Unit."],
                        .pedido-itens-table tbody td[data-label="Total"] {
                            display: inline-flex !important;
                            flex-direction: column !important;
                            width: 48% !important;
                            margin-bottom: 8px !important;
                            vertical-align: top !important;
                        }

                        .pedido-itens-table tbody td[data-label="Quantidade"]::before,
                        .pedido-itens-table tbody td[data-label="Unidade"]::before,
                        .pedido-itens-table tbody td[data-label="Preço Unit."]::before,
                        .pedido-itens-table tbody td[data-label="Total"]::before {
                            content: attr(data-label);
                            font-size: 10px;
                            color: #9CA3AF;
                            text-transform: uppercase;
                            font-weight: 600;
                            letter-spacing: 0.5px;
                            margin-bottom: 2px;
                        }

                        /* Total destaque */
                        .pedido-itens-table tbody td[data-label="Total"] {
                            font-weight: 700 !important;
                            color: #059669 !important;
                            font-size: 15px !important;
                        }

                        .pedido-itens-table tfoot tr {
                            display: flex !important;
                            justify-content: space-between !important;
                            align-items: center !important;
                            padding: 12px 14px !important;
                            border-radius: 10px !important;
                            margin-top: 4px !important;
                        }

                        .pedido-itens-table tfoot td {
                            padding: 0 !important;
                            font-size: 14px !important;
                        }

                        .pedido-itens-table tfoot td:last-child {
                            font-size: 16px !important;
                            text-align: right !important;
                        }

                        .pedido-itens-table tfoot td:first-child {
                            display: none !important;
                        }

                        /* Anexos table - mobile cards */
                        .pedido-anexos-table thead {
                            display: none !important;
                        }

                        .pedido-anexos-table tbody tr {
                            display: block !important;
                            background: #F9FAFB !important;
                            border: 1px solid #E5E7EB !important;
                            border-radius: 10px !important;
                            padding: 14px !important;
                            margin-bottom: 10px !important;
                            overflow: hidden !important;
                            max-width: 100% !important;
                            box-sizing: border-box !important;
                        }

                        .pedido-anexos-table tbody td {
                            display: block !important;
                            padding: 0 !important;
                            text-align: left !important;
                            border: none !important;
                        }

                        /* Tipo - destaque principal */
                        .pedido-anexos-table tbody td[data-label="Tipo"] {
                            font-size: 15px !important;
                            font-weight: 600 !important;
                            color: #111827 !important;
                            margin-bottom: 8px !important;
                            padding-bottom: 8px !important;
                            border-bottom: 1px solid #E5E7EB !important;
                        }

                        .pedido-anexos-table tbody td[data-label="Tipo"]::before {
                            display: none !important;
                        }

                        /* Arquivo - truncado */
                        .pedido-anexos-table tbody td[data-label="Arquivo"] {
                            margin-bottom: 10px !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                            max-width: calc(100vw - 80px) !important;
                            font-size: 12px !important;
                            color: #6B7280 !important;
                        }

                        .pedido-anexos-table tbody td[data-label="Arquivo"]::before {
                            content: attr(data-label);
                            font-size: 10px;
                            color: #9CA3AF;
                            text-transform: uppercase;
                            font-weight: 600;
                            letter-spacing: 0.5px;
                            display: block;
                            margin-bottom: 2px;
                        }

                        /* Data Envio, Vencimento, Parcela - empilhados */
                        .pedido-anexos-table tbody td[data-label="Data Envio"],
                        .pedido-anexos-table tbody td[data-label="Vencimento"],
                        .pedido-anexos-table tbody td[data-label="Nº Parcela"] {
                            display: block !important;
                            width: 100% !important;
                            margin-bottom: 6px !important;
                            box-sizing: border-box !important;
                            font-size: 14px !important;
                        }

                        .pedido-anexos-table tbody td[data-label="Data Envio"]::before,
                        .pedido-anexos-table tbody td[data-label="Vencimento"]::before,
                        .pedido-anexos-table tbody td[data-label="Nº Parcela"]::before {
                            content: attr(data-label);
                            font-size: 10px;
                            color: #9CA3AF;
                            text-transform: uppercase;
                            font-weight: 600;
                            letter-spacing: 0.5px;
                            margin-bottom: 2px;
                        }

                        /* Ações - botão no rodapé do card */
                        .pedido-anexos-table tbody td[data-label="Ações"] {
                            margin-top: 4px !important;
                            padding-top: 10px !important;
                            border-top: 1px solid #E5E7EB !important;
                        }

                        .pedido-anexos-table tbody td[data-label="Ações"]::before {
                            display: none !important;
                        }

                        .pedido-anexos-table tbody td[data-label="Ações"] a {
                            width: 100% !important;
                            display: block !important;
                            text-align: center !important;
                            padding: 10px !important;
                            border-radius: 8px !important;
                            font-size: 14px !important;
                        }

                        /* NF grid inside card */
                        .pedido-nf-grid {
                            grid-template-columns: 1fr !important;
                        }

                        .pedido-nf-chave {
                            font-size: 9px !important;
                            word-break: break-all !important;
                        }

                        /* Histórico */
                        .pedido-historico-item-header {
                            flex-direction: column !important;
                            gap: 2px !important;
                        }
                    }
                `}
            </style>
        </Template>
    );
};

export default PedidoCompraDetalhes;
